import React, { useEffect, useMemo, useState } from "react";
import Admin from "../layout/Admin";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getCounts, getIncome } from "../../actions/dashboard";
import { getPlan } from "../../actions/plan";
import { canAccess } from "../../utils/access";
import PrinterIncome from "./PrinterIncome";
import PrinterIncomeTable from "./PrinterIncomeTable";
import DailyIncomeCard from "./DailyIncomeCard";
import { dayTargetOf, money, targetNote } from "./incomeLabels";
import TargetBar from "../plan/TargetBar";
import {
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";

const totalIncome = (items = []) =>
  items.reduce(
    (sum, item) =>
      sum +
      (item.totalAmount || []).reduce(
        (printerTotal, amount) => printerTotal + Number(amount.totalAmount || 0),
        0
      ),
    0
  );

const toInputDate = (date) => format(date, "yyyy-MM-dd");

// A single day has no other to stand beside in the day by day chart, so it comes
// with the days before it
const DAILY_DAYS = 14;

const createRange = (start, end, label) => {
  const startDate = startOfDay(start);
  const endDate = end;
  const duration = endDate.getTime() - startDate.getTime();
  const previousEnd = new Date(startDate.getTime() - 1);
  const previousStart = new Date(previousEnd.getTime() - duration);
  const dailyStart =
    differenceInCalendarDays(endDate, startDate) === 0
      ? startOfDay(subDays(endDate, DAILY_DAYS - 1))
      : startDate;

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    previous: {
      startDate: previousStart.toISOString(),
      endDate: previousEnd.toISOString(),
    },
    daily: {
      startDate: dailyStart.toISOString(),
      endDate: endDate.toISOString(),
    },
    label,
  };
};

// Each as [start, end]. One that is over ends with its last day; the others run to now.
const quickRanges = [
  { key: "today", label: "Өнөөдөр", range: (now) => [now, now] },
  {
    key: "yesterday",
    label: "Өчигдөр",
    range: (now) => [subDays(now, 1), endOfDay(subDays(now, 1))],
  },
  { key: "7days", label: "7 хоног", range: (now) => [subDays(now, 6), now] },
  { key: "30days", label: "30 хоног", range: (now) => [subDays(now, 29), now] },
  { key: "month", label: "Энэ сар", range: (now) => [startOfMonth(now), now] },
  {
    key: "lastMonth",
    label: "Өмнөх сар",
    range: (now) => [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
  },
];

const Icon = ({ children, tone }) => (
  <div className={`flex items-center justify-center w-11 h-11 rounded-xl ${tone}`}>
    {children}
  </div>
);

const StatCard = ({ title, value, note, icon, tone, share }) => (
  <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700">
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-800 truncate dark:text-gray-100">
          {value}
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</p>
      </div>
      <Icon tone={tone}>{icon}</Icon>
    </div>
    {share !== null && share !== undefined && (
      <div className="mt-3">
        <TargetBar share={share} />
      </div>
    )}
  </div>
);

const Dashboard = ({ getCounts, getIncome, getPlan, user, dashboard, loading }) => {
  const now = new Date();
  const [startValue, setStartValue] = useState(toInputDate(now));
  const [endValue, setEndValue] = useState(toInputDate(now));
  const [activeRange, setActiveRange] = useState("today");
  const [dayTarget, setDayTarget] = useState(0);

  // The target comes from the Plan page, which needs its own access
  const canSeePlan = canAccess(user, "plan");

  useEffect(() => {
    const today = new Date();
    getCounts();
    getIncome(createRange(today, today, "Өнөөдөр"));
  }, [getCounts, getIncome]);

  useEffect(() => {
    if (!canSeePlan) return;
    getPlan().then((plan) => setDayTarget(dayTargetOf(plan)));
  }, [canSeePlan, getPlan]);

  const totals = useMemo(() => {
    const current = totalIncome(dashboard.printerIncome);
    const previous = totalIncome(dashboard.previousIncome);
    const change = previous ? ((current - previous) / previous) * 100 : null;
    return { current, previous, change };
  }, [dashboard.printerIncome, dashboard.previousIncome]);

  const activePrinters = (dashboard.printerIncome || []).filter(
    (printer) => totalIncome([printer]) > 0
  ).length;
  const average = activePrinters ? totals.current / activePrinters : 0;

  const selectQuickRange = (option) => {
    const [start, end] = option.range(new Date());
    setStartValue(toInputDate(start));
    setEndValue(toInputDate(end));
    setActiveRange(option.key);
    getIncome(createRange(start, end, option.label));
  };

  const submitCustomRange = (event) => {
    event.preventDefault();
    if (!startValue || !endValue) return;
    const start = new Date(`${startValue}T00:00:00`);
    const end = endOfDay(new Date(`${endValue}T00:00:00`));
    if (start > end) return;
    setActiveRange("custom");
    getIncome(createRange(start, end, "Сонгосон хугацаа"));
  };

  const periodLabel = dashboard.range?.label || "Өнөөдөр";
  const comparison =
    totals.change === null
      ? "Өмнөх хугацаанд орлого бүртгэгдээгүй"
      : `${totals.change >= 0 ? "+" : ""}${totals.change.toFixed(1)}% өмнөх хугацаанаас`;

  // The range's target is the daily one for each of its days. Without a target
  // to show, the card falls back to the period before.
  const days = dashboard.range
    ? differenceInCalendarDays(
        new Date(dashboard.range.endDate),
        new Date(dashboard.range.startDate)
      ) + 1
    : 1;
  const target = dayTarget * days;
  const share = target > 0 ? totals.current / target : null;

  return (
    <Admin>
      <div className="pb-10">
        <div className="flex flex-col mt-6 mb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold tracking-wide text-purple-600 uppercase dark:text-purple-400">
              CloudPrint хяналтын төв
            </p>
            <h1 className="mt-1 text-3xl font-bold text-gray-800 dark:text-gray-100">
              Хяналтын самбар
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Орлого болон системийн үндсэн үзүүлэлтүүд
            </p>
          </div>
          {dashboard.updatedAt && (
            <p className="mt-3 text-xs text-gray-400 sm:mt-0 dark:text-gray-500">
              Шинэчилсэн: {format(new Date(dashboard.updatedAt), "HH:mm")}
            </p>
          )}
        </div>

        <section className="p-4 mb-6 bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                Тайлангийн хугацаа
              </p>
              <div className="flex flex-wrap gap-2">
                {quickRanges.map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    onClick={() => selectQuickRange(option)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                      activeRange === option.key
                        ? "text-white bg-purple-600 shadow-sm"
                        : "text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={submitCustomRange} className="flex flex-col mt-4 sm:flex-row sm:items-end xl:mt-0">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Эхлэх огноо
                <input
                  type="date"
                  value={startValue}
                  max={endValue}
                  onChange={(event) => setStartValue(event.target.value)}
                  className="block w-full mt-1 text-sm text-gray-700 bg-white border-gray-300 rounded-lg dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600"
                />
              </label>
              <label className="mt-3 text-xs font-medium text-gray-500 sm:mt-0 sm:ml-3 dark:text-gray-400">
                Дуусах огноо
                <input
                  type="date"
                  value={endValue}
                  min={startValue}
                  max={toInputDate(new Date())}
                  onChange={(event) => setEndValue(event.target.value)}
                  className="block w-full mt-1 text-sm text-gray-700 bg-white border-gray-300 rounded-lg dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600"
                />
              </label>
              <button
                type="submit"
                disabled={loading || !startValue || !endValue || startValue > endValue}
                className="h-10 px-5 mt-3 text-sm font-semibold text-white bg-gray-800 rounded-lg sm:mt-0 sm:ml-3 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-800 dark:bg-gray-100"
              >
                Харах
              </button>
            </form>
          </div>
        </section>

        {dashboard.incomeError && (
          <div className="flex items-center justify-between px-4 py-3 mb-6 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50 dark:text-red-200 dark:bg-red-900 dark:border-red-700">
            <span>Орлогын мэдээллийг шинэчилж чадсангүй. Өмнөх мэдээллийг харуулж байна.</span>
            <button
              type="button"
              onClick={() => submitCustomRange({ preventDefault: () => {} })}
              className="ml-4 font-semibold underline focus:outline-none"
            >
              Дахин оролдох
            </button>
          </div>
        )}

        <div className="grid gap-4 mb-6 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title={`${periodLabel} · Нийт орлого`}
            value={money(totals.current)}
            note={share === null ? comparison : targetNote(totals.current, target, days)}
            share={share}
            tone="text-purple-600 bg-purple-100 dark:text-purple-200 dark:bg-purple-700"
            icon={<span className="text-lg font-bold">₮</span>}
          />
          <StatCard
            title="Орлоготой киоск"
            value={activePrinters}
            note={`Нийт ${dashboard.printerCount || 0} киоскоос`}
            tone="text-green-600 bg-green-100 dark:text-green-200 dark:bg-green-700"
            icon={<span className="text-xl">▣</span>}
          />
          <StatCard
            title="Киоскийн дундаж"
            value={money(average, { maximumFractionDigits: 1 })}
            note="Орлоготой киоск бүрээр"
            tone="text-blue-600 bg-blue-100 dark:text-blue-200 dark:bg-blue-700"
            icon={<span className="text-xl">↗</span>}
          />
          <StatCard
            title="Нийт хэрэглэгч"
            value={dashboard.userCount || 0}
            note="Бүртгэлтэй хэрэглэгч"
            tone="text-orange-600 bg-orange-100 dark:text-orange-200 dark:bg-orange-700"
            icon={<span className="text-xl">●</span>}
          />
        </div>

        <DailyIncomeCard
          daily={dashboard.dailyIncome}
          error={dashboard.dailyError}
          target={dayTarget}
        />

        <div className="relative p-5 mb-6 bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Киоскийн орлого</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Төрлөөр ангилсан орлого · Нийт орлогоор ихээс бага руу эрэмбэлсэн</p>
          </div>
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75 rounded-xl dark:bg-gray-800">
              <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <PrinterIncome printerData={dashboard.printerIncome} />
        </div>

        <PrinterIncomeTable printerData={dashboard.printerIncome} range={dashboard.range} />
      </div>
    </Admin>
  );
};

Icon.propTypes = { children: PropTypes.node.isRequired, tone: PropTypes.string.isRequired };
StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  note: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  tone: PropTypes.string.isRequired,
  share: PropTypes.number,
};
Dashboard.propTypes = {
  getCounts: PropTypes.func.isRequired,
  getIncome: PropTypes.func.isRequired,
  getPlan: PropTypes.func.isRequired,
  user: PropTypes.object,
  dashboard: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  dashboard: state.dashboard,
  loading: state.dashboard.loading,
});

export default connect(mapStateToProps, { getCounts, getIncome, getPlan })(Dashboard);
